import React from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineExclamationTriangle, HiOutlineInboxStack, HiOutlineLockClosed } from "react-icons/hi2";
import Button from "./Button";
import cn from "./cn";

// Four honest states are distinguished everywhere: loading, empty (no
// records), not permitted, and failed to load (with retry). A failed request
// must never be rendered as a zero or an empty list.

export const Skeleton = ({ className, lines = 1 }) =>
  lines > 1 ? (
    <div className={cn("space-y-2", className)} aria-hidden="true">
      {Array.from({ length: lines }).map((_, index) => (
        <div key={index} className="h-3.5 animate-pulse rounded bg-slate-200" style={{ width: `${90 - (index % 3) * 15}%` }} />
      ))}
    </div>
  ) : (
    <div className={cn("h-3.5 animate-pulse rounded bg-slate-200", className)} aria-hidden="true" />
  );

export const LoadingState = ({ label, className, compact = false }) => {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("flex items-center justify-center gap-3 text-sm text-slate-500", compact ? "py-6" : "py-14", className)}
    >
      <span className="ui-spinner h-5 w-5" aria-hidden="true" />
      <span>{label || t("ui.states.loading")}</span>
    </div>
  );
};

export const EmptyState = ({ icon: Icon = HiOutlineInboxStack, title, description, action, className, compact = false }) => {
  const { t } = useTranslation();
  return (
    <div className={cn("flex flex-col items-center justify-center text-center", compact ? "px-4 py-8" : "px-6 py-14", className)}>
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400">
        <Icon size={22} aria-hidden="true" />
      </span>
      <p className="mt-3 text-sm font-semibold text-slate-800">{title || t("ui.states.emptyTitle")}</p>
      {description ? <p className="mt-1 max-w-sm text-sm leading-6 text-slate-500">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
};

export const ErrorState = ({ title, description, onRetry, retryLabel, requestId, className, compact = false }) => {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className={cn("flex flex-col items-center justify-center text-center", compact ? "px-4 py-8" : "px-6 py-14", className)}
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-rose-50 text-rose-600">
        <HiOutlineExclamationTriangle size={22} aria-hidden="true" />
      </span>
      <p className="mt-3 text-sm font-semibold text-slate-800">{title || t("ui.states.errorTitle")}</p>
      <p className="mt-1 max-w-md text-sm leading-6 text-slate-500">{description || t("ui.states.errorDescription")}</p>
      {requestId ? (
        <p className="mt-1 text-xs text-slate-400">
          {t("ui.states.reference")}: <span className="ltr-value font-mono">{requestId}</span>
        </p>
      ) : null}
      {onRetry ? (
        <Button variant="secondary" size="sm" className="mt-4" onClick={onRetry}>
          {retryLabel || t("ui.states.retry")}
        </Button>
      ) : null}
    </div>
  );
};

export const NotPermittedState = ({ title, description, className, compact = false }) => {
  const { t } = useTranslation();
  return (
    <div className={cn("flex flex-col items-center justify-center text-center", compact ? "px-4 py-8" : "px-6 py-14", className)}>
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-50 text-amber-600">
        <HiOutlineLockClosed size={22} aria-hidden="true" />
      </span>
      <p className="mt-3 text-sm font-semibold text-slate-800">{title || t("ui.states.notPermittedTitle")}</p>
      <p className="mt-1 max-w-md text-sm leading-6 text-slate-500">{description || t("ui.states.notPermittedDescription")}</p>
    </div>
  );
};

// Localized message for the machine-readable error codes the API returns
// with a `field` (BW-ADM-019). Returns null for anything else, so the
// server's own message is used as before.
const localizedCodeMessage = (data, translate, fieldLabels = {}) => {
  const field = typeof data?.field === "string" ? data.field : "";
  const label = field
    ? fieldLabels[field] || translate(`ui.fields.${field}`, { defaultValue: field })
    : translate("ui.errors.aField");
  switch (data?.code) {
    case "PAYLOAD_TOO_LARGE_ERROR":
      return translate("ui.errors.fieldTooLarge", { field: label, limit: data.limitBytes });
    case "AIRLINE_ALREADY_EXISTS_ERROR":
      return translate(`ui.errors.airlineExists.${field || "other"}`, {
        defaultValue: translate("ui.errors.airlineExists.other"),
      });
    case "AIRLINE_ID_MISMATCH_ERROR":
      return translate("ui.errors.idMismatch");
    case "USER_AIRLINE_ACCESS_CONFLICT_ERROR":
      return translate("ui.errors.userAirlineAccessConflict");
    // Ad banner image refused by its bytes (JPG or PNG, at most 2 MB).
    case "AD_IMAGE_INVALID":
      return translate("ui.errors.adImageInvalid", { defaultValue: data.message });
    case "FST_ERR_VALIDATION": {
      if (!field || !data.rule) return null;
      const generic = translate("ui.errors.validation.invalid", { field: label });
      const byRule = translate(`ui.errors.validation.${data.rule}`, {
        field: label,
        limit: data.limit,
        defaultValue: generic,
      });
      return translate(`ui.errors.validation.${data.rule}_${field}`, {
        field: label,
        limit: data.limit,
        defaultValue: byRule,
      });
    }
    case "BAD_REQUEST_ERROR":
      return field ? translate("ui.errors.invalidField", { field: label }) : null;
    case "VALIDATION_ERROR":
      return field ? translate("ui.errors.invalidField", { field: label }) : null;
    default:
      return null;
  }
};

// Account-access and airline-assignment outcomes (DESIGN §1/§2) are explained
// by their code, whatever the HTTP status: a 403 here is a specific rule, not
// a missing role, so the generic "not allowed" text would hide the cause.
const ACCESS_RULE_CODES = Object.freeze({
  ACCOUNT_SUSPENDED: "ui.errors.accountSuspended",
  AGENCY_SUSPENDED: "ui.errors.agencySuspended",
  SESSION_EXPIRED: "ui.errors.sessionExpired",
  SELF_STATUS_CHANGE_FORBIDDEN: "ui.errors.selfStatusChange",
  ROLE_HIERARCHY_FORBIDDEN: "ui.errors.roleHierarchy",
  SELF_PERMISSION_CHANGE_FORBIDDEN: "ui.errors.selfPermissionChange",
  AIRLINE_ACCESS_NOT_APPLICABLE: "ui.errors.airlineAccessNotApplicable",
  AIRLINE_PERMISSION_NOT_GRANTABLE: "ui.errors.airlinePermissionNotGrantable",
});

const accessRuleMessage = (data, translate) => {
  const key = ACCESS_RULE_CODES[data?.code];
  if (!key) return null;
  // The grant refusal names each airline + permission; keep that detail.
  const detail =
    data.code === "AIRLINE_PERMISSION_NOT_GRANTABLE" && typeof data.message === "string"
      ? data.message.replace(/^airline permissions? not grantable:\s*/i, "").trim()
      : "";
  if (detail) {
    return translate("ui.errors.airlinePermissionNotGrantableDetail", { detail });
  }
  return translate(key);
};

// Extracts a user-facing message + request reference from an axios error.
// `fieldLabels` maps API field names to the form's own labels.
export const describeRequestError = (error, t, { fieldLabels } = {}) => {
  const status = Number(error?.response?.status || 0);
  const serverMessage = error?.response?.data?.message;
  const requestId = error?.requestId || error?.response?.data?.requestId;
  const translate = t || ((key) => key);
  let message;
  const accessRule = error?.response ? accessRuleMessage(error.response.data, translate) : null;
  if (error?.sessionExpired) {
    message = translate("ui.errors.sessionExpired");
  } else if (accessRule) {
    message = accessRule;
  } else if (status === 403) {
    message = translate("ui.errors.forbidden");
  } else if (status === 404) {
    message = serverMessage || translate("ui.errors.notFound");
  } else if (status >= 500) {
    message = translate("ui.errors.server");
  } else if (!error?.response) {
    message = translate("ui.errors.network");
  } else {
    message =
      localizedCodeMessage(error?.response?.data, translate, fieldLabels) ||
      serverMessage ||
      translate("ui.errors.generic");
  }
  return { status, message, requestId, serverMessage };
};

// Chooses between the four states for a data-loading block.
export const AsyncState = ({
  loading,
  error,
  forbidden,
  empty,
  onRetry,
  emptyTitle,
  emptyDescription,
  emptyAction,
  children,
  compact,
  loadingLabel,
}) => {
  const { t } = useTranslation();
  if (loading) return <LoadingState compact={compact} label={loadingLabel} />;
  if (forbidden || error?.response?.status === 403) return <NotPermittedState compact={compact} />;
  if (error) {
    const { message, requestId } = describeRequestError(error, t);
    return <ErrorState compact={compact} description={message} requestId={requestId} onRetry={onRetry} />;
  }
  if (empty) return <EmptyState compact={compact} title={emptyTitle} description={emptyDescription} action={emptyAction} />;
  return children;
};
